// React
import React from 'react';

// Mantine
import { MRT_ColumnDef, MRT_TableOptions } from 'mantine-react-table';

// Refine
import type { ListProps } from '@refinedev/mantine';
import { useDocumentTitle } from '@refinedev/react-router-v6';

// Custom Components
import { BillingDataGridWrapper } from 'components/common/BillingDataGridWrapper';
import { CreateModal } from 'components/common/CreateModal';
import { CreateRowButton } from 'components/common/CreateRowButton';
import { EditModal } from 'components/common/EditModal';
import { TableProgressBar } from 'components/common/TableProgressBar';

// Columns
import { AdminColumns } from 'columns/Admin';

// Interfaces
import { Admin } from 'interfaces';

// Resource
import { useCreateAdmin, useListAdmins, useUpdateAdmin } from 'api/resource';
import {
  CreateMutationDataParams,
  UpdateMutationDataParams,
  createMutationData,
  updateMutationData,
} from 'api/utils/MutationDataBuilder';
import { TableAlertBanner } from 'components/common/TableAlertBanner';

export const AdminManagement: React.FC<ListProps> = () => {
  const title = 'Admin Management';
  useDocumentTitle('Users Management | QBilling');

  const {
    data: admins = [] as Admin[],
    isLoading,
    isError,
    isFetching,
    error: adminError,
  } = useListAdmins();

  const createAdmin = useCreateAdmin();
  const updateAdmin = useUpdateAdmin();

  const handleCreateAdmin = async ({
    table,
    values,
  }: CreateMutationDataParams<Admin>) =>
    createMutationData<Admin>({
      table,
      values,
      mutation: createAdmin,
    });

  const handleEditAdmin = async ({
    table,
    values,
    row,
  }: UpdateMutationDataParams<Admin>) =>
    updateMutationData<Admin>({
      table,
      values,
      row,
      mutation: updateAdmin,
    });

  const data = admins;
  const columns: MRT_ColumnDef<Admin>[] = AdminColumns();

  const tableOptions: MRT_TableOptions<Admin> = {
    data,
    columns,
    state: {
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching,
    },
    initialState: {
      columnVisibility: {
        phone: false,
      },
    },
    createDisplayMode: 'modal',
    editDisplayMode: 'modal',
    enableFullScreenToggle: false,
    enableEditing: true,
    enableHiding: true,
    mantineEditRowModalProps: {
      size: 'lg',
      overlayProps: {
        opacity: 0.15,
        blur: 5,
      },
    },
    onCreatingRowSave: handleCreateAdmin,
    onEditingRowSave: handleEditAdmin,
    mantineProgressProps({ isTopToolbar }) {
      return TableProgressBar({ isTopToolbar });
    },
    mantineToolbarAlertBannerProps() {
      return TableAlertBanner({ isError, message: adminError?.message });
    },
    renderTopToolbarCustomActions: ({ table }) => {
      return CreateRowButton<Admin>({ type: 'role', table });
    },
    renderCreateRowModalContent: ({ table, row, internalEditComponents }) => {
      return CreateModal<Admin>({
        table,
        row,
        internalEditComponents,
        type: 'admin',
      });
    },
    renderEditRowModalContent: ({ table, row, internalEditComponents }) => {
      return EditModal<Admin>({
        table,
        row,
        internalEditComponents,
        type: 'admin',
      });
    },
  };

  return (
    <BillingDataGridWrapper
      title={title}
      tableOptions={tableOptions}
      dataTestID="adminManagement"
    />
  );
};
